<template>
  <footer
    class="print:hidden flex flex-col gap-y-5 bg-primary/5 py-8 border-b-8 border-primary mt-12"
  >
    <div class="soar-container flex flex-wrap justify-center gap-4 text-sm">
      <a
        v-for="link in footerLinks1"
        :key="link.path"
        :href="link.path"
        target="_blank"
        class="text-primary hover:underline underline-offset-2"
      >
        {{ $t(link.t9nkey) }}
      </a>
    </div>
    <div class="links-grid grid soar-container justify-center items-center">
      <div id="fed" class="w-full flex justify-center">
        <img src="@/assets/pngs/fed-auth.png" alt="fed" class="w-auto h-4" />
      </div>
      <div id="counter">
        <div class="w-full flex flex-col items-center gap-y-1">
          <span class="text-xs text-primary font-medium"> {{ $t('visitorCounter') }}</span>
          <div class="flex flex-col items-center">
            <span class="text-2xl tracking-wider text-primary-700 font-medium">
              {{ `${globalStore.visitorCount}` }}
            </span>
            <span class="text-xs text-primary font-medium"> {{ $t('visitors') }}</span>
          </div>
        </div>
      </div>
      <div id="social-media" class="w-full flex flex-col items-center gap-y-3">
        <span class="text-xs text-primary font-medium"> {{ $t('golden_socialmedia') }}</span>
        <div class="w-full flex justify-center gap-x-4">
          <a
            v-for="(socialMedia, key) in socialMediaLinks"
            :key="key"
            :class="socialMedia.classList"
            :href="socialMedia.href"
            target="_blank"
            class="w-8 h-8 rounded-full bg-primary flex justify-center items-center hover:shadow-md cursor-pointer"
          >
            <Component :is="socialMedia.icon" class="fill-white" />
          </a>
        </div>
      </div>
      <div id="call-center">
        <div class="flex flex-col items-center gap-y-1">
          <span class="text-xs text-primary font-medium"> {{ $t('golden_callcenter') }}</span>
          <div class="flex flex-col items-center">
            <h3 class="text-2xl tracking-wider text-primary-700 font-medium">600522222</h3>
            <span class="text-xs text-primary font-medium"> {{ $t('Available247') }}</span>
          </div>
        </div>
      </div>
      <div id="icons" class="w-full flex flex-col-reverse md:flex-row items-center gap-4">
        <a href="https://icp.gov.ae/en/about-us/ica-sustainability-efforts/" target="_blank">
          <img src="@/assets/pngs/sustainability.png" alt="sustainability" class="w-auto h-12" />
        </a>
        <a href="https://u.ae/en#/" target="_blank">
          <img src="@/assets/pngs/u_ae.png" alt="u_ae" class="w-auto h-12" />
        </a>
      </div>
    </div>
    <div class="soar-container flex flex-wrap justify-center gap-4 text-xs">
      <a
        v-for="link in footerLinks2"
        :key="link.path"
        :href="link.path"
        target="_blank"
        class="leading-4 text-primary hover:underline underline-offset-2 font-light"
      >
        {{ $t(link.t9nkey) }}
      </a>
    </div>
  </footer>
</template>

<script setup>
import { useGlobalStore } from '@/stores/global'
import { computed, onMounted } from 'vue'

import FacebookIcon from '@/assets/svgs/social/facebook-f.svg'
import InstagramIcon from '@/assets/svgs/social/instagram.svg'
import RssIcon from '@/assets/svgs/social/rss.svg'
import TwitterxIcon from '@/assets/svgs/social/twitter-x.svg'
import YoutubeIcon from '@/assets/svgs/social/youtube.svg'

const globalStore = useGlobalStore()

onMounted(async () => {
  await globalStore.getVisitorCount()
})

const footerLinks1 = computed(() => {
  return [
    {
      t9nkey: 'FAQMenu',
      path: 'https://icp.gov.ae/en/faq/',
      isExternal: true
    },
    {
      t9nkey: 'happinessFormula',
      path: 'https://icp.gov.ae/en/services/happiness-formula/',
      isExternal: true
    },
    {
      t9nkey: 'SiteMap',
      path: 'https://icp.gov.ae/en/sitemap/',
      isExternal: true
    },
    {
      t9nkey: 'contactUs',
      path: 'https://icp.gov.ae/en/contact-us/',
      isExternal: true
    },
    {
      t9nkey: 'archive',
      path: 'https://icp.gov.ae/en/archive/',
      isExternal: true
    },
    {
      t9nkey: 'icaEmail',
      path: 'https://webmail.emiratesid.ae/owa',
      isExternal: true
    },
    {
      t9nkey: 'suppliersInquiries',
      path: 'https://icp.gov.ae/en/suppliers-inquiries/',
      isExternal: true
    },
    {
      t9nkey: 'Help',
      path: 'https://icp.gov.ae/en/help/',
      isExternal: true
    }
  ]
})

const footerLinks2 = computed(() => {
  return [
    {
      t9nkey: 'Accessibility',
      path: 'https://icp.gov.ae/en/accessibility/',
      isExternal: true
    },
    {
      t9nkey: 'Disclaimer',
      path: 'https://icp.gov.ae/en/disclaimer/',
      isExternal: true
    },
    {
      t9nkey: 'TermsConditions',
      path: 'https://icp.gov.ae/en/terms-conditions/',
      isExternal: true
    },
    {
      t9nkey: 'privacypolicy',
      path: 'https://icp.gov.ae/en/privacy-policy/',
      isExternal: true
    },
    {
      t9nkey: 'copyright',
      path: 'https://icp.gov.ae/en/copyright/',
      isExternal: true
    },
    {
      t9nkey: 'ICATerminology',
      path: 'https://icp.gov.ae/en/federal-authority-for-identity-and-citizenship-terminology/',
      isExternal: true
    }
  ]
})

const socialMediaLinks = [
  {
    href: 'http://www.facebook.com/pages/Emirates-Identity-Authority-EIDA/214066365271853',
    icon: FacebookIcon,
    classList: 'p-2.5 hover:bg-[#0866ff]'
  },
  {
    href: 'https://www.instagram.com/uaeicp/',
    icon: InstagramIcon,
    classList: 'p-2 hover:bg-amber-800'
  },
  {
    href: 'https://www.youtube.com/c/IdentityandCitizenshipUAE_ICAUAE',
    icon: YoutubeIcon,
    classList: 'p-2 hover:bg-red-700'
  },
  { href: 'https://twitter.com/UAEICP', icon: TwitterxIcon, classList: 'p-2 hover:bg-black' },
  {
    href: 'http://www.emiratesid.gov.ae/handlers/public/eida/rss.aspx?lang=1',
    icon: RssIcon,
    classList: 'p-2 hover:bg-orange-700'
  }
  // { href: "", icon: WindowsIcon, classList: 'p-2 hover:bg-[#0067b8]' }
]
</script>

<style lang="scss" scoped>
.links-grid {
  //
  grid-template-areas: 'fed fed fed counter  social-media social-media social-media social-media social-media  call-center icons icons';
  justify-content: center;
  gap: 32px;

  #fed {
    justify-self: center;
    grid-area: fed;
  }
  #icons {
    justify-self: end;
    grid-area: icons;
  }
  #call-center {
    justify-self: center;
    grid-area: call-center;
  }
  #social-media {
    justify-self: center;
    grid-area: social-media;
  }

  /* Media query for mobile */
  // @media (max-width: 767px) {
  //   grid-template-columns: repeat(8, 1fr);
  //   grid-template-areas: 'fed fed fed counter  social-media social-media social-media social-media social-media  call-center icons icons';
  // }

  // /* Media query for tablet */
  // @media (max-width: 1023px) {
  //   grid-template-columns: repeat(12, 1fr);
  //   grid-template-areas: 'fed fed fed counter  social-media social-media social-media social-media social-media  call-center icons icons';
  //   // grid-template-areas: 'counter counter call-center call-center';
  // }

  // grid-template-areas:
  //         "logo logo logo logo logo logo logo logo logo logo . . . . . controller"
  //         "asset-info asset-info asset-info asset-info asset-info asset-info asset-info asset-info asset-info . . . . . . controller"
  //         "cta cta cta cta cta cta cta cta cta . . . . . . controller ";

  @media (max-width: 1023px) {
    grid-template-columns: repeat(12, 1fr);
    grid-template-areas:
      'fed fed fed counter counter counter call-center call-center call-center icons icons icons'
      'social-media social-media social-media social-media social-media social-media social-media social-media social-media social-media social-media social-media ';
  }

  @media screen and (max-width: 767px) {
    grid-template-columns: repeat(8, 1fr);
    grid-template-areas:
      'counter counter counter counter call-center call-center call-center call-center'
      'fed fed fed fed fed fed fed fed'
      'icons icons icons icons icons icons icons icons'
      'icons icons icons icons icons icons icons icons'
      'social-media social-media social-media social-media social-media social-media social-media social-media ';
  }
}
</style>
